import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatDate, formatGBP } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";

const STATUS = {
  intake: "Intake", in_progress: "In Progress", submitted: "Submitted",
  awaiting_decision: "Awaiting Decision", granted: "Granted", refused: "Refused", closed: "Closed",
};

const PRIORITY_CLS = {
  low: "bg-slate-100 text-slate-700", medium: "bg-blue-50 text-blue-800",
  high: "bg-amber-50 text-amber-800", urgent: "bg-red-50 text-red-800",
};

const INVOICE_CLS = {
  draft: "bg-slate-100 text-slate-700", sent: "bg-blue-50 text-blue-800",
  paid: "bg-green-50 text-green-800", written_off: "bg-slate-200 text-slate-500",
};

export default function MatterDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [matter, setMatter] = useState(null);
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [docs, setDocs] = useState([]);
  const [comms, setComms] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({});
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const loadAll = async () => {
    try {
      const [m, u, mt] = await Promise.all([
        api.get(`/matters/${id}`), api.get("/users"), api.get("/meta"),
      ]);
      setMatter(m.data); setUsers(u.data); setMeta(mt.data);
      setEditForm({
        title: m.data.title, client_id: m.data.client_id, matter_type: m.data.matter_type,
        status: m.data.status, assigned_to: m.data.assigned_to || "",
        fixed_fee: m.data.fixed_fee, deadline: m.data.deadline?.slice(0, 10) || "",
        description: m.data.description || "",
      });
      const [c, t, d, cm, inv] = await Promise.all([
        api.get(`/clients/${m.data.client_id}`),
        api.get(`/tasks?matter_id=${id}`),
        api.get(`/documents?matter_id=${id}`),
        api.get(`/communications?matter_id=${id}`),
        api.get(`/invoices?matter_id=${id}`),
      ]);
      setClient(c.data); setTasks(t.data); setDocs(d.data); setComms(cm.data); setInvoices(inv.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const saveMatter = async () => {
    try {
      const payload = { ...editForm, fixed_fee: Number(editForm.fixed_fee) || 0 };
      if (!payload.assigned_to) payload.assigned_to = null;
      if (!payload.deadline) payload.deadline = null;
      await api.put(`/matters/${id}`, payload);
      toast.success("Matter updated"); setEditing(false); loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteMatter = async () => {
    if (!window.confirm("Delete this matter and all associated data?")) return;
    try { await api.delete(`/matters/${id}`); toast.success("Matter deleted"); navigate("/matters"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!matter || !client) return <div className="text-sm text-slate-500">Loading matter…</div>;

  return (
    <div className="space-y-6">
      <Link to="/matters" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0B1528]">
        <ArrowLeft size={12} /> Back to matters
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1 font-mono-num">{matter.reference}</div>
          <h2 className="font-serif-brand text-3xl">{matter.title}</h2>
          <div className="mt-2 text-sm text-slate-600">
            <Link to="/clients" className="hover:underline">{client.full_name}</Link>
            {" · "}<span>{matter.matter_type}</span>
            {" · "}<span className="inline-block px-2 py-0.5 text-xs rounded-sm bg-slate-100">{STATUS[matter.status]}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="edit-matter" variant="outline" className="rounded-sm h-9" onClick={() => setEditing((v) => !v)}>
            {editing ? "Cancel" : "Edit"}
          </Button>
          <Button data-testid="delete-matter" variant="outline" className="rounded-sm h-9 text-red-700 hover:bg-red-50" onClick={deleteMatter}>
            <Trash size={14} className="mr-1" /> Delete
          </Button>
        </div>
      </div>

      {editing && (
        <div className="border border-slate-200 bg-white rounded-sm p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Title</Label>
            <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
            <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
              <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-sm bg-white">
                {(meta.matter_status || []).map((s) => <SelectItem key={s} value={s}>{STATUS[s] || s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Type</Label>
            <Select value={editForm.matter_type} onValueChange={(v) => setEditForm({ ...editForm, matter_type: v })}>
              <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-sm bg-white max-h-64">
                {(meta.matter_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Assigned to</Label>
            <Select value={editForm.assigned_to} onValueChange={(v) => setEditForm({ ...editForm, assigned_to: v })}>
              <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent className="rounded-sm bg-white">
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Deadline</Label>
            <Input type="date" value={editForm.deadline || ""} onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Fixed fee (£)</Label>
            <Input type="number" min="0" value={editForm.fixed_fee} onChange={(e) => setEditForm({ ...editForm, fixed_fee: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Description</Label>
            <Textarea rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div className="md:col-span-3">
            <Button onClick={saveMatter} className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B]">Save changes</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Fixed Fee" value={formatGBP(matter.fixed_fee)} />
        <Stat label="Deadline" value={formatDate(matter.deadline)} />
        <Stat label="Assigned" value={userMap[matter.assigned_to]?.name || "Unassigned"} />
        <Stat label="Opened" value={formatDate(matter.created_at)} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none p-0 h-auto w-full justify-start gap-6">
          {["overview", "tasks", "documents", "communications", "billing"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#C5A059] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-1 pb-3 text-sm capitalize">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-4">
          <div className="border border-slate-200 bg-white rounded-sm p-6">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">Description</div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{matter.description || "No description yet."}</p>
          </div>
          <div className="border border-slate-200 bg-white rounded-sm p-6">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">Client</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <Info label="Name" value={client.full_name} />
              <Info label="Nationality" value={client.nationality} />
              <Info label="Passport" value={client.passport_number} />
              <Info label="Email" value={client.email} />
              <Info label="Phone" value={client.phone} />
              <Info label="Date of birth" value={client.date_of_birth} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="mt-6">
          <TasksTab matterId={id} tasks={tasks} users={users} meta={meta} reload={loadAll} />
        </TabsContent>
        <TabsContent value="documents" className="mt-6">
          <DocsTab matterId={id} docs={docs} reload={loadAll} />
        </TabsContent>
        <TabsContent value="communications" className="mt-6">
          <CommsTab matterId={id} comms={comms} meta={meta} reload={loadAll} />
        </TabsContent>
        <TabsContent value="billing" className="mt-6">
          <BillingTab matterId={id} invoices={invoices} meta={meta} reload={loadAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 bg-white rounded-sm p-4">
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</div>
      <div className="text-lg font-medium mt-1 font-mono-num">{value || "—"}</div>
    </div>
  );
}
function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-wider uppercase text-slate-500">{label}</div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}

function TasksTab({ matterId, tasks, users, meta, reload }) {
  const [open, setOpen] = useState(false);
  const emp = { title: "", description: "", assigned_to: "", priority: "medium", status: "open", due_date: "" };
  const [f, setF] = useState(emp);
  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, matter_id: matterId };
      if (!payload.assigned_to) payload.assigned_to = null;
      if (!payload.due_date) payload.due_date = null;
      if (!payload.description) payload.description = null;
      await api.post("/tasks", payload);
      toast.success("Task added"); setOpen(false); setF(emp); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const toggle = async (t) => {
    try { await api.put(`/tasks/${t.id}`, { ...t, status: t.status === "done" ? "open" : "done" }); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/tasks/${id}`); reload(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-task-btn" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B] h-9"><Plus size={14} className="mr-1" /> New Task</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <Input placeholder="Task title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-sm" data-testid="task-title" />
              <Textarea rows={2} placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-sm" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-sm bg-white">
                    {(meta.task_priority || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="rounded-sm" />
              </div>
              <Select value={f.assigned_to} onValueChange={(v) => setF({ ...f, assigned_to: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Assign to…" /></SelectTrigger>
                <SelectContent className="rounded-sm bg-white">
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <DialogFooter><Button data-testid="save-task" type="submit" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B]">Create task</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border border-slate-200 bg-white rounded-sm divide-y divide-slate-200">
        {tasks.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No tasks yet.</div>}
        {tasks.map((t) => (
          <div key={t.id} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50">
            <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} className="w-4 h-4 accent-[#0B1528]" data-testid={`task-check-${t.id}`} />
            <div className="flex-1 min-w-0">
              <div className={`text-sm ${t.status === "done" ? "line-through text-slate-400" : ""}`}>{t.title}</div>
              {t.description && <div className="text-xs text-slate-500">{t.description}</div>}
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-sm ${PRIORITY_CLS[t.priority]}`}>{t.priority}</span>
            <div className="text-xs text-slate-500 w-24 text-right">{formatDate(t.due_date)}</div>
            <button onClick={() => remove(t.id)} className="text-slate-400 hover:text-red-700"><Trash size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocsTab({ matterId, docs, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", doc_type: "", notes: "" });
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/documents", { ...f, matter_id: matterId, notes: f.notes || null }); toast.success("Document added"); setOpen(false); setF({ name: "", doc_type: "", notes: "" }); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => { try { await api.delete(`/documents/${id}`); reload(); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-doc-btn" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B] h-9"><Plus size={14} className="mr-1" /> Add Document</Button></DialogTrigger>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader><DialogTitle>Document metadata</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <Input required placeholder="Document name (e.g. Passport scan)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-sm" data-testid="doc-name" />
              <Input required placeholder="Document type (e.g. Passport, Application, Evidence)" value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} className="rounded-sm" data-testid="doc-type" />
              <Textarea rows={2} placeholder="Notes / location on shared drive" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-sm" />
              <DialogFooter><Button data-testid="save-doc" type="submit" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B]">Save</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border border-slate-200 bg-white rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Type</th>
              <th className="text-left px-4 py-2 font-medium">Notes</th>
              <th className="text-left px-4 py-2 font-medium">Added</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {docs.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No documents recorded.</td></tr>}
            {docs.map((d) => (
              <tr key={d.id} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-medium">{d.name}</td>
                <td className="px-4 py-2 text-slate-600">{d.doc_type}</td>
                <td className="px-4 py-2 text-slate-500 text-xs">{d.notes || "—"}</td>
                <td className="px-4 py-2 text-slate-500">{formatDate(d.uploaded_at)}</td>
                <td className="px-4 py-2 text-right"><button onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-700"><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CommsTab({ matterId, comms, meta, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ comm_type: "email", subject: "", summary: "", participants: "", occurred_at: "" });
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/communications", {
        ...f, matter_id: matterId,
        summary: f.summary || null, participants: f.participants || null,
        occurred_at: f.occurred_at ? new Date(f.occurred_at).toISOString() : null,
      });
      toast.success("Communication logged"); setOpen(false);
      setF({ comm_type: "email", subject: "", summary: "", participants: "", occurred_at: "" });
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => { try { await api.delete(`/communications/${id}`); reload(); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-comm-btn" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B] h-9"><Plus size={14} className="mr-1" /> Log Communication</Button></DialogTrigger>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader><DialogTitle>Log communication</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Select value={f.comm_type} onValueChange={(v) => setF({ ...f, comm_type: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-sm bg-white">
                    {(meta.comm_type || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="datetime-local" value={f.occurred_at} onChange={(e) => setF({ ...f, occurred_at: e.target.value })} className="rounded-sm" />
              </div>
              <Input required placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="rounded-sm" data-testid="comm-subject" />
              <Input placeholder="Participants" value={f.participants} onChange={(e) => setF({ ...f, participants: e.target.value })} className="rounded-sm" />
              <Textarea rows={3} placeholder="Summary of the conversation" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} className="rounded-sm" />
              <DialogFooter><Button data-testid="save-comm" type="submit" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B]">Save log</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2">
        {comms.length === 0 && <div className="border border-slate-200 bg-white rounded-sm p-8 text-center text-sm text-slate-500">No communications logged.</div>}
        {comms.map((c) => (
          <div key={c.id} className="border border-slate-200 bg-white rounded-sm p-4 flex gap-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 w-20 shrink-0">{c.comm_type}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{c.subject}</div>
              {c.participants && <div className="text-xs text-slate-500">With: {c.participants}</div>}
              {c.summary && <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{c.summary}</div>}
            </div>
            <div className="text-xs text-slate-500 shrink-0 text-right">
              {formatDate(c.occurred_at)}
              <button onClick={() => remove(c.id)} className="block mt-2 text-slate-400 hover:text-red-700"><Trash size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BillingTab({ matterId, invoices, meta, reload }) {
  const [open, setOpen] = useState(false);
  const emp = { line_items: [{ description: "Legal fees", amount: 0 }], status: "draft", notes: "", due_at: "" };
  const [f, setF] = useState(emp);

  const setLine = (i, k, v) => {
    const items = [...f.line_items];
    items[i] = { ...items[i], [k]: k === "amount" ? Number(v) : v };
    setF({ ...f, line_items: items });
  };
  const addLine = () => setF({ ...f, line_items: [...f.line_items, { description: "", amount: 0 }] });
  const removeLine = (i) => setF({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) });

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        matter_id: matterId, status: f.status,
        line_items: f.line_items.filter((l) => l.description),
        notes: f.notes || null,
        due_at: f.due_at ? new Date(f.due_at).toISOString() : null,
      };
      await api.post("/invoices", payload);
      toast.success("Invoice created"); setOpen(false); setF(emp); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const changeStatus = async (inv, status) => {
    try { await api.put(`/invoices/${inv.id}`, { ...inv, status }); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => { try { await api.delete(`/invoices/${id}`); reload(); } catch (err) { toast.error(formatApiError(err)); } };

  const total = f.line_items.reduce((a, l) => a + (Number(l.amount) || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-invoice-btn" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B] h-9"><Plus size={14} className="mr-1" /> New Invoice</Button></DialogTrigger>
          <DialogContent className="rounded-sm max-w-2xl">
            <DialogHeader><DialogTitle>New fixed-fee invoice</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div className="border border-slate-200 rounded-sm divide-y divide-slate-200">
                {f.line_items.map((li, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center p-2">
                    <Input placeholder="Description" value={li.description} onChange={(e) => setLine(i, "description", e.target.value)} className="col-span-8 rounded-sm" />
                    <Input type="number" placeholder="£" value={li.amount} onChange={(e) => setLine(i, "amount", e.target.value)} className="col-span-3 rounded-sm" />
                    <button type="button" onClick={() => removeLine(i)} className="col-span-1 text-slate-400 hover:text-red-700"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" onClick={addLine} className="rounded-sm h-8 text-xs"><Plus size={12} className="mr-1" /> Add line</Button>

              <div className="grid grid-cols-2 gap-3">
                <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-sm bg-white">
                    {(meta.invoice_status || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" placeholder="Due date" value={f.due_at} onChange={(e) => setF({ ...f, due_at: e.target.value })} className="rounded-sm" />
              </div>
              <Textarea rows={2} placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-sm" />
              <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                <div className="text-sm text-slate-500">Total</div>
                <div className="text-lg font-medium font-mono-num">{formatGBP(total)}</div>
              </div>
              <DialogFooter><Button data-testid="save-invoice" type="submit" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B]">Create invoice</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Invoice #</th>
              <th className="text-left px-4 py-2 font-medium">Issued</th>
              <th className="text-left px-4 py-2 font-medium">Due</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium">Total</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {invoices.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No invoices yet.</td></tr>}
            {invoices.map((inv) => (
              <tr key={inv.id} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-mono-num text-xs">{inv.invoice_number}</td>
                <td className="px-4 py-2 text-slate-600">{formatDate(inv.issued_at)}</td>
                <td className="px-4 py-2 text-slate-600">{formatDate(inv.due_at)}</td>
                <td className="px-4 py-2">
                  <Select value={inv.status} onValueChange={(v) => changeStatus(inv, v)}>
                    <SelectTrigger className={`rounded-sm h-7 text-xs border-none ${INVOICE_CLS[inv.status]}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-sm bg-white">
                      {(meta.invoice_status || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-2 text-right font-mono-num">{formatGBP(inv.total)}</td>
                <td className="px-4 py-2 text-right"><button onClick={() => remove(inv.id)} className="text-slate-400 hover:text-red-700"><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
